/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import Description from "App/model/description";
import {RenderElement} from "App/model/elements/element";
import DrawStyle from "App/model/draw_style";
import Group from "App/model/group";

export default
class GroupTree {

    private _modelDescription: Description;
    private _gtRoot: Group;
    private _groupIDToGroupMap: any;

    constructor(aModelDescription: Description) {
        this._modelDescription = aModelDescription;

        this._gtRoot = new Group(aModelDescription);

        this._groupIDToGroupMap = {};
        this._groupIDToGroupMap[this._gtRoot.getID()] = this._gtRoot;
    }

    /**
     *  Returns an unique group id.
     *  Used for groups generated during a run-time.
     *  @return {int} Group id
     *
     */
    public getUniqueGroupId() {
        let maxId = -1;
        for (let g in this._groupIDToGroupMap) {
            let n = Number(g);
            if (n > maxId) {
                maxId = n;
            }
        }

        maxId++;
        return maxId;
    }

    public setModelName(aModelName: string) {
        if (this._gtRoot) {
            this._gtRoot.data.name = aModelName;
        }
    }

    public getRoot(): Group {
        return this._gtRoot;
    }

    public serialize(): any {
        if (!this._gtRoot) {
            return null;
        }

        return this._gtRoot.serialize();
    }

    public addElement(aGroupID: number, aRenderElement: RenderElement) {
        let group = this.findGroup(aGroupID);
        if (group) {
            return group.addElement(aRenderElement);
        }
    }

    public removeElement(aRenderElement: RenderElement) {
        let group = aRenderElement.getGroup();
        if (group) {
            group.removeElement(aRenderElement);
        }
    }

    public getAllElements(): RenderElement[] {
        let renderElements = [];

        this._gtRoot.getAllElements(renderElements);

        return renderElements;
    }

    public findGroup(aGroupID: number): Group {
        let group = this._groupIDToGroupMap[aGroupID];
        if (group !== undefined) {
            return group;
        }

        return null;
    }

    public setSelected(aValue: boolean) {
        for (let g in this._groupIDToGroupMap) {
            let group = this._groupIDToGroupMap[g];
            group.setSelected(aValue);
        }
    }

    public addGroup(aParentGroup: any, aGroupJSON): number {
        let parentGroup = aParentGroup;
        if (!parentGroup) {
            parentGroup = this._gtRoot;
        }

        let newGroup = new Group(this._modelDescription);
        newGroup.parse(aGroupJSON);
        parentGroup.addChild(newGroup);

        // create hash map for fast accessing required group by its id
        this._groupIDToGroupMap[newGroup.getID()] = newGroup;

        return newGroup.getID();
    }

    public removeGroup(aGroupID: number) {
        let group = this._groupIDToGroupMap[aGroupID];
        if (!group) {
            return;
        }

        group.data.parent.removeChild(group);
        delete this._groupIDToGroupMap[aGroupID];
    }

    public parse(aModelDescriptionJSON: any): boolean {
        if (!aModelDescriptionJSON.hasOwnProperty("GroupTree")) {
            return false;
        }
        if (!aModelDescriptionJSON.GroupTree.hasOwnProperty("Groups")) {
            return false;
        }

        for (let i in aModelDescriptionJSON.GroupTree.Groups) {
            let groupJSON = aModelDescriptionJSON.GroupTree.Groups[i];
            this.addGroup(this._gtRoot, groupJSON);
        }

        return true;
    }

    public getJSONRepresentation(): any {
        return {
            Groups: [
                this._gtRoot.getJSONRepresentation()
            ]
        };
    }
}
